<template>
  <div>
    <article class="slo-card" v-bind="$attrs" v-on="$listeners">
      <div class="slo-card--header">
        <div v-if="hasSlot('overlay')" class="slo-card--overlay">
          <slot name="overlay" />
        </div>
        <a v-if="url" :href="url" :target="target" :rel="rel" class="slo-card--image">
          <slot name="image" />
        </a>
        <div v-else class="slo-card--image">
          <slot name="image"></slot>
        </div>
        <div class="slo-card--actions">
          <slot name="actions" />
        </div>
      </div>
      <div class="slo-card--content">
        <h2 class="slo-card--title" v-if="hasSlot('title')">
          <a v-if="url" :href="url" :target="target" :rel="rel">
            <slot name="title" />
          </a>
          <slot v-else name="title" />
        </h2>
        <a v-if="bodyLink" :href="url" :target="target" :rel="rel" class="body-link">
          <slot />
        </a>
        <slot v-else />
      </div>
      <div class="slo-card--add-to-cart-wrapper" v-if="hasSlot('add-to-cart')">
        <slot name="add-to-cart" />
      </div>
    </article>
    <slot name="modal" />
  </div>
</template>

<script>
export default {
  name: 'SloCard',
  props: {
    url: String|Boolean,
    target: String,
    rel: String,
    bodyLink: Boolean
  },
  methods: {
    hasSlot(name = 'default') {
      return Boolean(this.$slots[name] || this.$scopedSlots[name]);
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/variables';

slo-card {
  box-sizing: border-box;
  border: 1px solid rgba($gray, 0.5);
  background-clip: padding-box;
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  // font size variable? component-specific scaling?
  font-size: 16px;
  background-color: #fafafa;
  border-radius: 8px;
  transition: background-color 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
  // For some reason 100% breaks in IE11, but this works
  width: calc(99.999% / 2);
  height: 100%;

  @media screen and (min-width: $screen-sm-min) {
    width: calc(99.999% / 3);
  }

  &::before {
    content: '';
    display: block;
    width: 100%;
    padding-bottom: 141.6%;
  }

  [slot='image'] {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    background-color: #fafafa;

    img {
      display: block;
      position: absolute;
      width: 100%;
      min-width: 100%;
      max-width: 100%;
      height: 100%;
      visibility: hidden;
    }
  }

  & > * {
    display: none;
  }
}

.slo-card {
  box-sizing: border-box;
  border: 1px solid rgba($gray, 0.5);
  background-clip: padding-box;
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  // font size variable? component-specific scaling?
  font-size: 16px;
  background-color: #fff;
  border-radius: 8px;
  transition: background-color 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
  // For some reason 100% breaks in IE11, but this works
  width: calc(99.999% / 2);
  height: 100%;

  @media screen and (min-width: $screen-sm-min) {
    width: calc(99.999% / 3);
  }

  @media screen and (min-width: $screen-md-min) {
    width: calc(99.999% / 4);

    // smooth shadow transitions on hover
    // https://tobiasahlin.com/blog/how-to-animate-box-shadow/
    &::before {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1);
      opacity: 0;
      transition: opacity 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
      pointer-events: none;
      border-radius: 8px;
      content: '';
    }
  }

  @supports (display: grid) {
    box-sizing: content-box;
    width: auto;
    height: 100%;
    max-width: unset;
    margin-left: 0 !important;
    border: 1px solid rgba($gray, 0.5);
  }

  &:hover {
    background-color: #fff;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover .slo-card--title {
    color: $sloan-blue;
  }

  &:focus-within {
    &::before {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1);
      opacity: 0;
      transition: opacity 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
      pointer-events: none;
      border-radius: 8px;
      content: '';
    }
  }

  .slo-card--content {
    position: relative;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    margin-top: 0;
    padding: 16px;
    background: none;
    user-select: none;

    p:only-child {
      margin: 0;
    }

    a {
      color: inherit;
      transition: none;

      &:hover {
        color: $sloan-blue;
      }
    }

    a.body-link:hover {
      color: inherit;
    }

    a.spec-sheet-link {
      color: $sloan-blue;
    }
  }

  &--header {
    position: relative;
    width: 100%;
    background-color: #fafafa;
    border-radius: 8px 8px 0 0;
    overflow: hidden;

    &::before {
      content: '';
      display: block;
      width: 100%;
      padding-bottom: 100%;
    }
  }

  .slo-card--overlay {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
  }

  .slo-card--image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    background-color: #fafafa;
  }

  .slo-card--image img {
    display: block;
    position: absolute;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .slo-card--content > :last-child {
    margin-bottom: 0;
  }

  .slo-card--title {
    margin: 0 0 10px 0;
    font-size: 1em;
    font-weight: $medium; // would like to see a more descriptive variable like fw-medium
    transition: color 300ms cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  .slo-card--add-to-cart-wrapper {
    padding: 0 16px 16px;

    @media (min-width: $screen-md-min) {
      padding: 16px;
    }
  }
}
</style>
